import React, { useState, useEffect, useMemo, useCallback } from "react";
import hosys, { getMemToken } from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import { recordTemperature, getTemperatureRecords, EQUIPMENT_LABELS } from "@/components/services/temperatureService";
import EquipmentManager from "@/components/temperatures/EquipmentManager";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";
import EmptyState from "@/components/shared/EmptyState";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Thermometer, Calendar, CheckCircle2, AlertCircle, Settings,
  ChevronLeft, ChevronRight, Download, CalendarDays, CalendarRange,
  Pencil, Trash2, X, Check
} from "lucide-react";
import moment from "moment";
import "moment/locale/ca";
moment.locale("ca");

// ─── Edit Record Inline ───────────────────────────────────────────────────────
function EditableRecord({ record, onSave, onDelete, onCancel }) {
  const [editTemp, setEditTemp] = useState(record.temperature.toString());
  const [saving, setSaving] = useState(false);

  const tempNum = parseFloat(editTemp);
  const isOutOfRange = !isNaN(tempNum) && (tempNum < record.min_limit || tempNum > record.max_limit);
  const newStatus = isNaN(tempNum) ? record.status : (isOutOfRange ? "incorrect" : "correct");

  const handleSave = async () => {
    if (isNaN(tempNum)) return;
    setSaving(true);
    try {
      await hosys.entities.TemperatureRecord.update(record.id, {
        temperature: tempNum,
        status: newStatus,
        last_edited_at: new Date().toISOString(),
      });
      onSave();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await hosys.entities.TemperatureRecord.delete(record.id);
      onDelete();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1">
      <Input
        type="number"
        step="0.1"
        value={editTemp}
        onChange={(e) => setEditTemp(e.target.value)}
        className={`w-16 h-7 text-xs text-center font-bold p-1 ${isOutOfRange ? "border-red-300 bg-red-50 text-red-600" : ""}`}
        autoFocus
      />
      <span className="text-[10px] text-gray-400">°C</span>
      <button onClick={handleSave} disabled={isNaN(tempNum) || saving} className="p-1 rounded text-emerald-600 hover:bg-emerald-100 disabled:opacity-30">
        <Check className="w-3.5 h-3.5" />
      </button>
      <button onClick={handleDelete} disabled={saving} className="p-1 rounded text-red-500 hover:bg-red-100">
        <Trash2 className="w-3.5 h-3.5" />
      </button>
      <button onClick={onCancel} className="p-1 rounded text-gray-400 hover:bg-gray-100">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ─── Equipment Daily Card ─────────────────────────────────────────────────────
function EquipDayCard({ eq, todayRecords, venueId, user, onSaved, selectedDate }) {
  const [temp, setTemp] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const eqRecords = todayRecords.filter(r =>
    (r.equipment_name || "").toLowerCase() === (eq.name || "").toLowerCase() || r.equipment_config_id === eq.id
  );
  const lastRecord = eqRecords.length > 0 ? eqRecords[eqRecords.length - 1] : null;
  const allCorrect = eqRecords.every(r => r.status === "correct");
  const hasIncorrect = eqRecords.some(r => r.status === "incorrect");

  const tempNum = parseFloat(temp);
  const isOutOfRange = !isNaN(tempNum) && (tempNum < eq.min_limit || tempNum > eq.max_limit);

  const handleSave = async () => {
    if (isNaN(tempNum)) return;
    setSaving(true);
    try {
      await recordTemperature(venueId, user, {
        equipment_name: eq.name,
        equipment_type: eq.equipment_type,
        temperature: tempNum,
        min_limit: eq.min_limit,
        max_limit: eq.max_limit,
      }, selectedDate);
      setTemp("");
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => { if (e.key === "Enter") handleSave(); };

  return (
    <Card className={`border shadow-sm overflow-hidden transition-all ${
      hasIncorrect ? "border-red-200 bg-red-50/30" :
      eqRecords.length > 0 && allCorrect ? "border-emerald-200 bg-emerald-50/30" :
      "border-gray-100"
    }`} data-testid={`equip-card-${eq.id}`}>
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h4 className="font-semibold text-gray-900 text-sm">{eq.name}</h4>
            <p className="text-[11px] text-gray-400">{EQUIPMENT_LABELS[eq.equipment_type]} · {eq.min_limit}° a {eq.max_limit}°C</p>
          </div>
          {eqRecords.length > 0 && (
            <div className={`px-2 py-1 rounded-lg text-xs font-bold ${
              hasIncorrect ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"
            }`}>
              {lastRecord?.temperature}°C
            </div>
          )}
        </div>

        {/* Quick record history - editable */}
        {eqRecords.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {eqRecords.map((r) => (
              editingId === r.id ? (
                <EditableRecord
                  key={r.id}
                  record={r}
                  onSave={() => { setEditingId(null); onSaved(); }}
                  onDelete={() => { setEditingId(null); onSaved(); }}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <button
                  key={r.id}
                  onClick={() => setEditingId(r.id)}
                  className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium cursor-pointer hover:ring-2 hover:ring-[#7B2D3F]/20 transition-all ${
                    r.status === "correct" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"
                  }`}
                  title="Fes clic per editar"
                >
                  {r.temperature}°C · {moment(r.created_date).format("HH:mm")}
                  <Pencil className="w-2.5 h-2.5 opacity-40" />
                </button>
              )
            ))}
          </div>
        )}

        {/* Quick input */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Input
              type="number"
              step="0.1"
              placeholder="°C"
              value={temp}
              onChange={(e) => setTemp(e.target.value)}
              onKeyDown={handleKeyDown}
              className={`text-center font-bold pr-8 h-10 ${isOutOfRange ? "border-red-300 bg-red-50 text-red-600" : ""}`}
              data-testid={`temp-input-${eq.id}`}
            />
            <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs">°C</span>
          </div>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={isNaN(tempNum) || saving}
            className="bg-[#7B2D3F] hover:bg-[#5B1D2F] h-10 px-4"
            data-testid={`temp-save-${eq.id}`}
          >
            {saving ? "..." : isOutOfRange ? (
              <AlertCircle className="w-4 h-4" />
            ) : (
              <CheckCircle2 className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>
    </Card>
  );
}

// ─── Monthly Report (Grid) ────────────────────────────────────────────────────
function MonthlyReport({ venueId, equipments }) {
  const [month, setMonth] = useState(moment());
  const [allRecords, setAllRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadMonth = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(
        `${API_URL}/api/temperatures/monthly/${venueId}?year=${month.year()}&month=${month.month() + 1}`,
        {
          credentials: 'include',
          headers: getMemToken() ? { 'Authorization': `Bearer ${getMemToken()}` } : {},
        }
      );
      if (res.ok) {
        const records = await res.json();
        setAllRecords(records);
      }
    } catch (err) {
      console.error('Error loading monthly temps:', err);
    }
    setLoading(false);
  }, [venueId, month]);

  useEffect(() => {
    loadMonth();
  }, [loadMonth]);

  // Build equipment columns: configured cold equipment + any extra from records
  const gridEquipments = useMemo(() => {
    // Start with configured cold equipments
    const coldTypes = new Set(["fridge", "freezer", "chamber", "cold_reception"]);
    const configured = equipments.filter(eq => coldTypes.has(eq.equipment_type));
    const configuredNames = new Set(configured.map(eq => eq.name.toLowerCase()));

    // Detect any additional equipment names from records not in configured
    const extraNames = new Set();
    for (const r of allRecords) {
      const name = r.equipment_name || "";
      if (name && !configuredNames.has(name.toLowerCase())) {
        extraNames.add(name);
      }
    }

    // Create pseudo-equipment entries for extras, use limits from records if available
    const extras = [...extraNames].sort().map(name => {
      const sampleRec = allRecords.find(r => r.equipment_name === name);
      return {
        id: `extra_${name}`,
        name,
        equipment_type: sampleRec?.equipment_type || "unknown",
        min_limit: sampleRec?.min_limit ?? "?",
        max_limit: sampleRec?.max_limit ?? "?",
      };
    });

    return [...configured, ...extras];
  }, [equipments, allRecords]);

  const days = useMemo(() => {
    const result = [];
    const daysInMonth = month.daysInMonth();
    for (let d = 1; d <= daysInMonth; d++) {
      const date = month.clone().date(d).format("YYYY-MM-DD");
      if (moment(date).isAfter(moment())) break;
      const dayRecs = allRecords.filter(r => r.date === date);
      result.push({ date, day: d, dayRecs });
    }
    return result;
  }, [allRecords, month.format("YYYY-MM")]);

  const totalRecords = allRecords.length;
  const correctCount = allRecords.filter(r => r.status === "correct").length;
  const incorrectCount = totalRecords - correctCount;
  const pct = totalRecords > 0 ? Math.round((correctCount / totalRecords) * 100) : 0;

  const exportCSV = () => {
    const header = "Data,Equip,Tipus,Temperatura,Min,Max,Estat,Hora,Responsable\n";
    const rows = allRecords.map(r =>
      `${r.date},${r.equipment_name},${EQUIPMENT_LABELS[r.equipment_type] || r.equipment_type},${r.temperature},${r.min_limit},${r.max_limit},${r.status === "correct" ? "OK" : "FORA RANG"},${moment(r.created_date).format("HH:mm")},${r.responsible_name || ""}`
    ).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `temperatures_${month.format("YYYY-MM")}.csv`;
    a.click();
  };

  return (
    <div className="space-y-4" data-testid="monthly-report">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button onClick={() => setMonth(m => m.clone().subtract(1, "month"))} className="p-1.5 rounded-lg hover:bg-gray-100">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-semibold capitalize min-w-[140px] text-center">{month.format("MMMM YYYY")}</span>
          <button onClick={() => setMonth(m => m.clone().add(1, "month"))}
            disabled={month.isSame(moment(), "month")}
            className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <Button variant="outline" size="sm" onClick={exportCSV} className="gap-1.5 text-xs" data-testid="export-monthly-csv">
          <Download className="w-3.5 h-3.5" /> Exportar CSV
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-4 gap-2">
        <Card className="p-3 border-0 shadow-sm text-center"><p className="text-xl font-bold">{totalRecords}</p><p className="text-[10px] text-gray-500">Total</p></Card>
        <Card className="p-3 border-0 shadow-sm text-center bg-emerald-50"><p className="text-xl font-bold text-emerald-600">{correctCount}</p><p className="text-[10px] text-emerald-600">OK</p></Card>
        <Card className="p-3 border-0 shadow-sm text-center bg-red-50"><p className="text-xl font-bold text-red-600">{incorrectCount}</p><p className="text-[10px] text-red-600">Fora rang</p></Card>
        <Card className="p-3 border-0 shadow-sm text-center"><p className="text-xl font-bold text-[#7B2D3F]">{pct}%</p><p className="text-[10px] text-gray-500">Compliment</p></Card>
      </div>

      {loading ? <QuickLoader /> : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="w-full text-xs border-collapse" data-testid="monthly-table">
            <thead>
              <tr className="bg-[#7B2D3F]">
                <th className="text-left p-2.5 font-semibold text-white border-r border-[#5B1D2F] sticky left-0 bg-[#7B2D3F] z-10 min-w-[70px]">
                  Dia
                </th>
                {gridEquipments.map(eq => (
                  <th key={eq.id} className="text-center p-2 font-semibold text-white border-r border-[#5B1D2F] min-w-[110px]">
                    <span className="block truncate text-[11px]">{eq.name}</span>
                    <span className="text-[9px] text-white/60 font-normal">{eq.min_limit}° a {eq.max_limit}°C</span>
                  </th>
                ))}
                {equipments.filter(eq => !["fridge", "freezer", "chamber", "cold_reception"].includes(eq.equipment_type)).length > 0 && (
                  <th className="text-center p-2 font-semibold text-white min-w-[90px]">Altres</th>
                )}
              </tr>
            </thead>
            <tbody>
              {days.map(({ date, day, dayRecs }, rowIdx) => {
                const isWeekend = [0, 6].includes(moment(date).day());
                const otherEquips = equipments.filter(eq => !["fridge", "freezer", "chamber", "cold_reception"].includes(eq.equipment_type));
                const gridNames = new Set(gridEquipments.map(eq => eq.name.toLowerCase()));
                const otherRecs = dayRecs.filter(r => !gridNames.has((r.equipment_name || "").toLowerCase()));
                return (
                  <tr key={date} className={`${isWeekend ? "bg-gray-50/80" : ""} hover:bg-amber-50/30 transition-colors`}>
                    <td className={`p-2 font-semibold border-r border-gray-200 border-b sticky left-0 z-10 ${isWeekend ? "bg-gray-50" : "bg-white"}`}>
                      <span className="text-gray-800">{day}</span>
                      <span className="text-[9px] text-gray-400 ml-1 capitalize">{moment(date).format("dd")}</span>
                    </td>
                    {gridEquipments.map(eq => {
                      const recs = dayRecs.filter(r => (r.equipment_name || "").toLowerCase() === eq.name.toLowerCase());
                      if (recs.length === 0) {
                        return (
                          <td key={eq.id} className="text-center p-1.5 border-r border-gray-100 border-b">
                            <span className="text-gray-300 text-[10px]">—</span>
                          </td>
                        );
                      }
                      return (
                        <td key={eq.id} className="p-1 border-r border-gray-100 border-b">
                          <div className="flex flex-wrap gap-0.5 justify-center">
                            {recs.map((r, i) => {
                              const isOk = r.status === "correct";
                              return (
                                <span
                                  key={`${r.temperature_record_id || i}`}
                                  className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold leading-tight ${
                                    isOk
                                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                      : "bg-red-50 text-red-700 border border-red-300"
                                  }`}
                                  title={`${r.temperature}°C — ${moment(r.created_date).format("HH:mm")} — ${r.responsible_name || ""}`}
                                >
                                  {r.temperature}°
                                </span>
                              );
                            })}
                          </div>
                        </td>
                      );
                    })}
                    {otherEquips.length > 0 && (
                      <td className="text-center p-1.5 border-b">
                        {otherRecs.length > 0 ? (
                          <div className="flex flex-wrap gap-0.5 justify-center">
                            {otherRecs.map((r, i) => (
                              <span
                                key={`row-${i}`}
                                className={`inline-block px-1 py-0.5 rounded text-[9px] font-medium ${
                                  r.status === "correct" ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"
                                }`}
                                title={`${r.equipment_name}: ${r.temperature}°C`}
                              >
                                {r.temperature}°
                              </span>
                            ))}
                          </div>
                        ) : <span className="text-gray-300 text-[10px]">—</span>}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {days.length === 0 && (
            <div className="text-center py-8 text-gray-400 text-sm">Sense registres per aquest mes</div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Annual Report ────────────────────────────────────────────────────────────
function AnnualReport({ venueId }) {
  const [year, setYear] = useState(moment().year());
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadYear();
  }, [year, venueId]);

  const loadYear = async () => {
    setLoading(true);
    const data = [];
    for (let m = 0; m < 12; m++) {
      const monthMoment = moment().year(year).month(m);
      if (monthMoment.isAfter(moment())) break;
      const daysInMonth = monthMoment.daysInMonth();
      let total = 0, correct = 0;
      for (let d = 1; d <= daysInMonth; d++) {
        const date = monthMoment.clone().date(d).format("YYYY-MM-DD");
        if (moment(date).isAfter(moment())) break;
        const dayRecs = await getTemperatureRecords(venueId, date);
        total += dayRecs.length;
        correct += dayRecs.filter(r => r.status === "correct").length;
      }
      data.push({
        month: monthMoment.format("MMMM"),
        total,
        correct,
        incorrect: total - correct,
        pct: total > 0 ? Math.round((correct / total) * 100) : 0,
      });
    }
    setMonthlyData(data);
    setLoading(false);
  };

  const yearTotal = monthlyData.reduce((a, m) => a + m.total, 0);
  const yearCorrect = monthlyData.reduce((a, m) => a + m.correct, 0);
  const yearPct = yearTotal > 0 ? Math.round((yearCorrect / yearTotal) * 100) : 0;

  const exportAnnualCSV = () => {
    const header = "Mes,Total,Correctes,Incorrectes,Compliment %\n";
    const rows = monthlyData.map(m => `${m.month},${m.total},${m.correct},${m.incorrect},${m.pct}%`).join("\n");
    const totalRow = `\nTOTAL,${yearTotal},${yearCorrect},${yearTotal - yearCorrect},${yearPct}%`;
    const blob = new Blob([header + rows + totalRow], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `temperatures_anual_${year}.csv`;
    a.click();
  };

  return (
    <div className="space-y-4" data-testid="annual-report">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button onClick={() => setYear(y => y - 1)} className="p-1.5 rounded-lg hover:bg-gray-100">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-semibold min-w-[60px] text-center">{year}</span>
          <button onClick={() => setYear(y => y + 1)} disabled={year >= moment().year()}
            className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <Button variant="outline" size="sm" onClick={exportAnnualCSV} className="gap-1.5 text-xs" data-testid="export-annual-csv">
          <Download className="w-3.5 h-3.5" /> Exportar CSV
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Card className="p-3 border-0 shadow-sm text-center"><p className="text-2xl font-bold">{yearTotal}</p><p className="text-[10px] text-gray-500">Registres totals</p></Card>
        <Card className="p-3 border-0 shadow-sm text-center bg-emerald-50"><p className="text-2xl font-bold text-emerald-600">{yearCorrect}</p><p className="text-[10px] text-emerald-600">Correctes</p></Card>
        <Card className="p-3 border-0 shadow-sm text-center"><p className="text-2xl font-bold text-[#7B2D3F]">{yearPct}%</p><p className="text-[10px] text-gray-500">Compliment anual</p></Card>
      </div>

      {loading ? <QuickLoader /> : (
        <div className="space-y-2">
          {monthlyData.map((m, i) => (
            <div key={`member-${i}`} className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-white">
              <span className="text-sm font-semibold text-gray-700 capitalize w-24">{m.month}</span>
              <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${m.pct >= 90 ? "bg-emerald-500" : m.pct >= 70 ? "bg-amber-500" : "bg-red-500"}`}
                  style={{ width: `${m.pct}%` }}
                />
              </div>
              <div className="flex items-center gap-3 text-xs flex-shrink-0">
                <span className="text-gray-500">{m.total} reg.</span>
                {m.incorrect > 0 && <span className="text-red-600 font-medium">{m.incorrect} err.</span>}
                <span className={`font-bold ${m.pct >= 90 ? "text-emerald-600" : m.pct >= 70 ? "text-amber-600" : "text-red-600"}`}>
                  {m.pct}%
                </span>
              </div>
            </div>
          ))}
          {monthlyData.length === 0 && (
            <div className="text-center py-8 text-gray-400 text-sm">Sense dades per a aquest any</div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Temperature Page ────────────────────────────────────────────────────
export default function Temperatures() {
  const { currentVenue, user, loading: venueLoading } = useVenue();
  const [records, setRecords] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState(moment().format("YYYY-MM-DD"));

  useEffect(() => {
    if (currentVenue) {
      loadRecords();
      loadEquipments();
    }
  }, [currentVenue, dateFilter]);

  const loadRecords = async () => {
    setLoading(true);
    const recs = await getTemperatureRecords(currentVenue.id, dateFilter);
    setRecords(recs);
    setLoading(false);
  };

  const loadEquipments = async () => {
    const eqs = await hosys.entities.EquipmentConfig.filter({ venue_id: currentVenue.id, active: true });
    setEquipments(eqs.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
  };

  if (venueLoading) return <QuickLoader />;

  const isToday = dateFilter === moment().format("YYYY-MM-DD");
  const correctCount = records.filter(r => r.status === "correct").length;
  const incorrectCount = records.filter(r => r.status === "incorrect").length;

  // Equipment that have no record for today
  const recordedEquipNames = new Set(records.map(r => (r.equipment_name || "").toLowerCase()));
  const pendingEquips = equipments.filter(eq => !recordedEquipNames.has((eq.name || "").toLowerCase()));
  const completedEquips = equipments.filter(eq => recordedEquipNames.has((eq.name || "").toLowerCase()));

  return (
    <div className="space-y-5" data-testid="temperatures-page">
      <PageHeader
        title="Control de Temperatures"
        subtitle="Registra i consulta les temperatures dels equips"
      />

      <Tabs defaultValue="daily" className="w-full">
        <TabsList className="w-full flex bg-gray-100/80 rounded-xl p-1 gap-1">
          <TabsTrigger value="daily" className="flex-1 rounded-lg text-xs gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Calendar className="w-3.5 h-3.5" /> Diari
          </TabsTrigger>
          <TabsTrigger value="monthly" className="flex-1 rounded-lg text-xs gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <CalendarDays className="w-3.5 h-3.5" /> Mensual
          </TabsTrigger>
          <TabsTrigger value="annual" className="flex-1 rounded-lg text-xs gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <CalendarRange className="w-3.5 h-3.5" /> Anual
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex-1 rounded-lg text-xs gap-1.5 data-[state=active]:bg-white data-[state=active]:shadow-sm">
            <Settings className="w-3.5 h-3.5" /> Equips
          </TabsTrigger>
        </TabsList>

        {/* DAILY VIEW */}
        <TabsContent value="daily" className="space-y-4 mt-4">
          {/* Date navigator */}
          <div className="flex items-center gap-3">
            <button onClick={() => setDateFilter(d => moment(d).subtract(1, "day").format("YYYY-MM-DD"))} className="p-1.5 rounded-lg hover:bg-gray-100">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2">
              <Input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="w-auto text-sm" />
              <span className="text-sm font-medium text-gray-600 capitalize">{moment(dateFilter).format("dddd")}</span>
            </div>
            <button onClick={() => setDateFilter(d => moment(d).add(1, "day").format("YYYY-MM-DD"))}
              disabled={isToday}
              className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30">
              <ChevronRight className="w-4 h-4" />
            </button>
            {!isToday && (
              <Button variant="ghost" size="sm" onClick={() => setDateFilter(moment().format("YYYY-MM-DD"))}>Avui</Button>
            )}
          </div>

          {/* Summary stats */}
          <div className="grid grid-cols-3 gap-2">
            <Card className="p-3 border-0 shadow-sm text-center">
              <p className="text-2xl font-bold text-gray-900">{records.length}</p>
              <p className="text-[10px] text-gray-500">Registres</p>
            </Card>
            <Card className="p-3 border-0 shadow-sm text-center bg-emerald-50">
              <p className="text-2xl font-bold text-emerald-600">{correctCount}</p>
              <p className="text-[10px] text-emerald-600">OK</p>
            </Card>
            <Card className={`p-3 border-0 shadow-sm text-center ${incorrectCount > 0 ? "bg-red-50" : ""}`}>
              <p className={`text-2xl font-bold ${incorrectCount > 0 ? "text-red-600" : "text-gray-300"}`}>{incorrectCount}</p>
              <p className={`text-[10px] ${incorrectCount > 0 ? "text-red-600" : "text-gray-400"}`}>Fora rang</p>
            </Card>
          </div>

          {/* Pending equipment — show for any date */}
          {pendingEquips.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">
                Pendents ({pendingEquips.length})
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {pendingEquips.map(eq => (
                  <EquipDayCard key={eq.id} eq={eq} todayRecords={records} venueId={currentVenue.id} user={user} onSaved={loadRecords} selectedDate={dateFilter} />
                ))}
              </div>
            </div>
          )}

          {/* Completed equipment */}
          {completedEquips.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-2">
                {isToday ? "Completats" : "Registrats"} ({completedEquips.length})
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {completedEquips.map(eq => (
                  <EquipDayCard key={eq.id} eq={eq} todayRecords={records} venueId={currentVenue.id} user={user} onSaved={loadRecords} selectedDate={dateFilter} />
                ))}
              </div>
            </div>
          )}

          {/* Unmatched records (equipment names from records that don't match configured equipment) */}
          {(() => {
            const configuredNames = new Set(equipments.map(eq => (eq.name || "").toLowerCase()));
            const unmatchedRecords = records.filter(r => !configuredNames.has((r.equipment_name || "").toLowerCase()));
            if (unmatchedRecords.length === 0) return null;
            const groupedByEquip = {};
            unmatchedRecords.forEach(r => {
              const name = r.equipment_name || "Desconegut";
              if (!groupedByEquip[name]) groupedByEquip[name] = [];
              groupedByEquip[name].push(r);
            });
            return (
              <div>
                <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">
                  Altres registres ({unmatchedRecords.length})
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {Object.entries(groupedByEquip).map(([eqName, recs]) => (
                    <Card key={`unmatched-${eqName}`} className="p-4 border-0 shadow-sm">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-semibold text-sm text-gray-900">{eqName}</p>
                          <p className="text-[10px] text-gray-400">Equip no configurat</p>
                        </div>
                      </div>
                      <div className="space-y-1">
                        {recs.map((r, ri) => (
                          <div key={`urec-${eqName}-${ri}`} className={`flex items-center justify-between px-2 py-1 rounded text-xs ${r.status === "correct" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                            <span className="font-bold">{r.temperature}°C</span>
                            <span className="text-[10px]">{r.status === "correct" ? "OK" : "Fora rang"}</span>
                          </div>
                        ))}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Non-equipment records */}
          {loading ? (
            <QuickLoader />
          ) : equipments.length === 0 ? (
            <EmptyState
              icon={Thermometer}
              title="Sense equips configurats"
              description="Ves a la pestanya 'Equips' per configurar els equips del teu establiment."
            />
          ) : records.length === 0 && equipments.length > 0 && !isToday ? (
            <EmptyState icon={Thermometer} title="Sense registres" description="No hi ha registres per aquesta data." />
          ) : null}
        </TabsContent>

        {/* MONTHLY VIEW */}
        <TabsContent value="monthly" className="mt-4">
          <MonthlyReport venueId={currentVenue?.id} equipments={equipments} />
        </TabsContent>

        {/* ANNUAL VIEW */}
        <TabsContent value="annual" className="mt-4">
          <AnnualReport venueId={currentVenue?.id} />
        </TabsContent>

        {/* EQUIPMENT SETTINGS */}
        <TabsContent value="settings" className="mt-4">
          <EquipmentManager
            venueId={currentVenue?.id}
            equipments={equipments}
            onRefresh={loadEquipments}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
